// PrivacyAccess.jsx
import { useEffect, useRef } from "react";
import { motion, useInView, useAnimation } from "framer-motion";

/* ── Icons ── */
const EyeIcon = ({ className = "w-8 h-8 text-[#093A5B]" }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const UserCogIcon = ({ className = "w-8 h-8 text-[#093A5B]" }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="18" cy="15" r="3" />
    <circle cx="9" cy="7" r="4" />
    <path d="M10 15H6a4 4 0 0 0-4 4v2" />
    <path d="m21.7 16.4-.9-.3" />
    <path d="m15.2 13.9-.9-.3" />
    <path d="m16.6 18.7.3-.9" />
    <path d="m19.1 12.2.3-.9" />
    <path d="m19.6 18.7-.4-1" />
    <path d="m16.8 12.3-.4-1" />
    <path d="m14.3 16.6 1-.4" />
    <path d="m20.7 13.8 1-.4" />
  </svg>
);

const FileCheckIcon = ({ className = "w-8 h-8 text-[#093A5B]" }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="m9 15 2 2 4-4" />
  </svg>
);

const ShieldIcon = ({ className = "w-8 h-8 text-[#093A5B]" }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
  </svg>
);

const features = [
  {
    title: "Control who sees what",
    description:
      "Decide which team members can view and edit different information.",
    icon: UserCogIcon,
    mt: "sm:mt-0",
  },
  {
    title: "Clear boundaries",
    description:
      "Work stays organized in separate spaces with proper access limits.",
    icon: EyeIcon,
    mt: "sm:mt-8",
  },
  {
    title: "Track activity",
    description: "See who did what and when for complete transparency.",
    icon: FileCheckIcon,
    mt: "sm:mt-0",
  },
  {
    title: "Privacy built-in",
    description: "Data protection is part of how we design every feature.",
    icon: ShieldIcon,
    mt: "sm:mt-8",
  },
];

const PrivacyAccess = () => {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [controls, isInView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        damping: 15,
        stiffness: 80,
        duration: 0.5,
      },
    },
  };
  // security.css Line 1156 to 1225
  return (
    <section className="PrivacyAccess px-6 py-20 lg:py-28 bg-gradient-to-b from-white via-[#093A5B]/[0.02] to-white relative overflow-hidden">
      {/* Grid Background */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          transition={{ duration: 1 }}
          style={{
            backgroundImage: `
              linear-gradient(rgba(9, 58, 91, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(9, 58, 91, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />
      </div>

      {/* Animated Floating Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 bg-[#093A5B]/5 rounded-full blur-3xl"
          animate={{
            x: [0, 100, 0],
            y: [0, 50, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-80 h-80 bg-[#093A5B]/5 rounded-full blur-3xl"
          animate={{
            x: [0, -80, 0],
            y: [0, -60, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />
      </div>

      <div className="mx-auto max-w-7xl relative z-10" ref={ref}>
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={controls}
          variants={{
            visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
          }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, rotate: -10 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{
              duration: 0.6,
              type: "spring",
              damping: 12,
              stiffness: 100,
            }}
            className="inline-block mb-6"
          >
            <motion.div
              className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#093A5B] to-[#093A5B]/70 flex items-center justify-center shadow-2xl"
              whileHover={{
                scale: 1.05,
                rotate: 5,
                transition: { duration: 0.3 },
              }}
            >
              <EyeIcon className="w-10 h-10 text-white" />
            </motion.div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="PrivacyAccess_heading"
          >
            Privacy & Access control
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="PrivacyAccess_paragraph"
          >
            You control who sees what—and we make it simple
          </motion.p>
        </motion.div>

        {/* Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="grid sm:grid-cols-2 gap-6"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              // relative bg-white rounded-3xl p-8 border border-[#093A5B]/10 hover:border-[#093A5B]/30 shadow-lg hover:shadow-2xl transition-all duration-300 group
              className={`PrivacyAccess_Card`}
            >
              {/* Hover Gradient Overlay */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-[#093A5B]/5 to-transparent rounded-3xl pointer-events-none"
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              />

              {/* Icon Container */}
              <motion.div
                className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-[#093A5B]/10 to-[#093A5B]/5 group-hover:from-[#093A5B]/20 group-hover:to-[#093A5B]/10 flex items-center justify-center mb-6 transition-all duration-300"
                whileHover={{
                  scale: 1.1,
                  rotate: [0, -5, 5, 0],
                  transition: { duration: 0.4 },
                }}
              >
                <feature.icon />
              </motion.div>

              {/* Title */}
              <motion.h3
                className="relative text-xl font-bold text-black mb-3 group-hover:text-[#093A5B] transition-colors duration-300"
                whileHover={{ x: 5 }}
              >
                {feature.title}
              </motion.h3>

              {/* Description */}
              <motion.p
                className="relative text-gray-600 leading-relaxed"
                whileHover={{ x: 3 }}
              >
                {feature.description}
              </motion.p>

              {/* Animated Bottom Border */}
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#093A5B] via-[#093A5B]/50 to-transparent rounded-b-3xl"
                initial={{ scaleX: 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                style={{ transformOrigin: "left" }}
              />

              {/* Decorative Corner Glow */}
              <motion.div
                className="absolute -top-1 -right-1 w-12 h-12 bg-gradient-to-br from-[#093A5B]/5 to-transparent rounded-full blur-xl"
                whileHover={{ scale: 1.2, opacity: 0.8 }}
                transition={{ duration: 0.3 }}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Custom CSS Animations */}
      <style jsx>{`
        .PrivacyAccess {
          --privacy-primary: #093a5b;
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) translateX(0px);
          }
          50% {
            transform: translateY(-20px) translateX(10px);
          }
        }

        @keyframes pulse-ring {
          0% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          100% {
            transform: scale(1.2);
            opacity: 0;
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        @keyframes gradient-shift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-pulse-ring {
          animation: pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        .animate-shimmer {
          animation: shimmer 2s infinite;
        }

        .animate-gradient {
          background-size: 200% auto;
          animation: gradient-shift 3s ease infinite;
        }

        /* Hover Effects */
        .group:hover .group-hover\:scale-105 {
          transform: scale(1.05);
        }

        /* Responsive Adjustments */
        @media (max-width: 640px) {
          .PrivacyAccess {
            padding-left: 1rem;
            padding-right: 1rem;
          }
        }

        @media (min-width: 768px) and (max-width: 1024px) {
          .PrivacyAccess {
            padding-left: 2rem;
            padding-right: 2rem;
          }
        }

        /* Smooth Scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Custom Scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb {
          background: var(--privacy-primary);
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #0a4a6e;
        }

        /* Selection Style */
        ::selection {
          background: var(--privacy-primary);
          color: white;
        }
      `}</style>
    </section>
  );
};

export default PrivacyAccess;
